/**
 * The questions visitors actually ask before they come up, with the owner's
 * answers. Entries store i18n *keys*, never text, so one list serves all five
 * locales — the same approach `data/searchIndex.ts` takes.
 *
 * `id` is the anchor the entry renders with (`/refuge/faq#pets`), so it is a
 * public URL: rename one and any link to it breaks. Order is editorial and is
 * the order the page renders — questions run from what you need before you set
 * off, through what is on site, to what you can borrow once you are there.
 */
export interface FaqEntry {
    /**
     * Stable anchor id. Lowercase, hyphenated, never reused for another
     * question — and never equal to a group's id, since both render on the
     * same page and a duplicate id sends the browser to whichever comes first.
     * `tests/searchIndex.test.ts` asserts the two sets stay disjoint.
     */
    id: string;
    /** i18n key for the question. */
    questionKey: string;
    /** i18n key for the answer. */
    answerKey: string;
    /** i18n key for the heading this question sits under. */
    groupKey: string;
    /** Phosphor icon name. */
    icon: string;
}

export const faq: FaqEntry[] = [
    // ── Contact ───────────────────────────────────────────────────
    {
        id: "phone",
        questionKey: "faqContactQuestion",
        answerKey: "faqContactAnswer",
        groupKey: "faqGroupContact",
        icon: "ph:phone",
    },

    // ── Getting there & arrival ───────────────────────────────────
    {
        id: "parking",
        questionKey: "faqParkingQuestion",
        answerKey: "faqParkingAnswer",
        groupKey: "faqGroupArrival",
        icon: "ph:car",
    },
    {
        id: "winter-road",
        questionKey: "faqWinterRoadQuestion",
        answerKey: "faqWinterRoadAnswer",
        groupKey: "faqGroupArrival",
        icon: "ph:road-horizon",
    },
    {
        id: "check-in",
        questionKey: "faqCheckInQuestion",
        answerKey: "faqCheckInAnswer",
        groupKey: "faqGroupArrival",
        icon: "ph:clock",
    },

    // ── On-site facilities ────────────────────────────────────────
    {
        id: "wifi",
        questionKey: "faqWifiQuestion",
        answerKey: "faqWifiAnswer",
        groupKey: "faqGroupFacilities",
        icon: "ph:wifi-high",
    },
    {
        id: "electricity",
        questionKey: "faqElectricityQuestion",
        answerKey: "faqElectricityAnswer",
        groupKey: "faqGroupFacilities",
        icon: "ph:lightning",
    },
    {
        id: "water",
        questionKey: "faqWaterQuestion",
        answerKey: "faqWaterAnswer",
        groupKey: "faqGroupFacilities",
        icon: "ph:drop",
    },
    {
        id: "bathrooms",
        questionKey: "faqBathroomsQuestion",
        answerKey: "faqBathroomsAnswer",
        groupKey: "faqGroupFacilities",
        icon: "ph:shower",
    },
    {
        id: "laundry",
        questionKey: "faqLaundryQuestion",
        answerKey: "faqLaundryAnswer",
        groupKey: "faqGroupFacilities",
        icon: "ph:t-shirt",
    },

    // ── Food ──────────────────────────────────────────────────────
    {
        id: "diets",
        questionKey: "faqDietsQuestion",
        answerKey: "faqDietsAnswer",
        groupKey: "faqGroupFood",
        icon: "ph:leaf",
    },
    {
        id: "supplies",
        questionKey: "faqSuppliesQuestion",
        answerKey: "faqSuppliesAnswer",
        groupKey: "faqGroupFood",
        icon: "ph:basket",
    },

    // ── House rules ───────────────────────────────────────────────
    {
        id: "dogs",
        questionKey: "faqDogsQuestion",
        answerKey: "faqDogsAnswer",
        groupKey: "faqGroupRules",
        icon: "ph:dog",
    },
    {
        id: "smoking",
        questionKey: "faqSmokingQuestion",
        answerKey: "faqSmokingAnswer",
        groupKey: "faqGroupRules",
        icon: "ph:cigarette",
    },
    {
        id: "children",
        questionKey: "faqChildrenQuestion",
        answerKey: "faqChildrenAnswer",
        groupKey: "faqGroupRules",
        icon: "ph:baby",
    },
    {
        id: "accessibility",
        questionKey: "faqAccessibilityQuestion",
        answerKey: "faqAccessibilityAnswer",
        groupKey: "faqGroupRules",
        icon: "ph:wheelchair",
    },

    // ── Gear & activities ─────────────────────────────────────────
    {
        id: "gear-rental",
        questionKey: "faqGearQuestion",
        answerKey: "faqGearAnswer",
        groupKey: "faqGroupGear",
        icon: "ph:backpack",
    },
    {
        id: "guides",
        questionKey: "faqGuidesQuestion",
        answerKey: "faqGuidesAnswer",
        groupKey: "faqGroupGear",
        icon: "ph:compass",
    },
    {
        id: "bedding",
        questionKey: "faqBeddingQuestion",
        answerKey: "faqBeddingAnswer",
        groupKey: "faqGroupGear",
        icon: "ph:bed",
    },
];

/**
 * The anchor each group renders with, so a whole section can be linked to:
 * `/refuge/faq#food` for the food questions, the way `#diets` reaches one of
 * them. Declared rather than slugified from the key, because these are public
 * URLs — deriving them would let a rename of an i18n key silently break every
 * link that points here.
 *
 * They share a page with the entry ids and so share one namespace. "Gear &
 * activities" owns `gear` and the rental question is `gear-rental`; the contact
 * group owns `contact` and its one question is `phone`.
 */
const GROUP_IDS: Record<string, string> = {
    faqGroupContact: "contact",
    faqGroupArrival: "arrival",
    faqGroupFacilities: "facilities",
    faqGroupFood: "food",
    faqGroupRules: "rules",
    faqGroupGear: "gear",
};

export interface FaqGroup {
    /** Stable anchor id for the whole section. */
    id: string;
    /** i18n key for the heading. */
    groupKey: string;
    entries: FaqEntry[];
}

/**
 * The same entries bucketed by `groupKey`, in first-appearance order — what the
 * page renders. Grouping here rather than in the template keeps the ordering
 * rule in one place, next to the list it orders.
 */
export const faqGroups: FaqGroup[] = faq.reduce((groups, entry) => {
    const group = groups.find(candidate => candidate.groupKey === entry.groupKey);
    if (group) group.entries.push(entry);
    else groups.push({ id: GROUP_IDS[entry.groupKey], groupKey: entry.groupKey, entries: [entry] });
    return groups;
}, [] as FaqGroup[]);
